import type { InvoiceAddress } from '../../../flow/user/userModels';
import type { HybrisAddress } from '../../../models/hybris/hybrisAddress';
import type { HybrisUserCustomerGroups } from '../../../models/hybris/hybrisUserCustomerGroups';
import type { HybrisUserInfo } from '../../../models/hybris/hybrisUserInfo';
import type { HybrisUserOrganisationUnit } from '../../../models/hybris/hybrisUserOrganisationUnit';
import type { HybrisCompensationVoucher } from '../../../models/hybris/hybrisVoucher';
import type {
    ApiCreateInvoiceAddressRequest,
    ApiCreateUserAddressRequest,
    ApiUpdateInvoiceAddressRequest,
    ApiUpdateUserAddressRequest,
} from './hybrisUserDirectApiRequests';

import getDirectHybrisAxiosClient from '../../clients/directHybrisAxiosClient';
import { hybrisFormValidationInterceptor } from './hybrisUserDirectApiErrorInterceptors';

export const hybrisUserDirectApiUrls = {
    getHybrisUserInfo: (shoppingUserId: string) => `/users/${shoppingUserId}`,
    updateHybrisUserInfo: (shoppingUserId: string) => `/users/${shoppingUserId}`,
    getUserPunchoutUnit: (shoppingUserId: string) => `/users/${shoppingUserId}`,
    getCustomerGroups: (shoppingUserId: string) => `/users/${shoppingUserId}/customergroups`,
    getCompensationVouchers: (shoppingUserId: string) =>
        `/vouchers/compensation/users/${shoppingUserId}`,
    getAllAddresses: (shoppingUserId: string) => `/users/${shoppingUserId}/addresses`,
    createAddress: (shoppingUserId: string) => `/users/${shoppingUserId}/addresses`,
    updateAddress: (shoppingUserId: string, addressId: string) =>
        `/users/${shoppingUserId}/addresses/${addressId}`,
    deleteAddress: (shoppingUserId: string, addressId: string) =>
        `/users/${shoppingUserId}/addresses/${addressId}`,
    getAllPunchoutAddresses: (unitId: string) => `/b2b/unit/${unitId}/addresses`,
    getInvoiceAddress: (shoppingUserId: string) => `/users/${shoppingUserId}/addresses/invoice`,
    createInvoiceAddress: (shoppingUserId: string) => `/users/${shoppingUserId}/addresses/invoice`,
    updateInvoiceAddress: (shoppingUserId: string, addressId: string) =>
        `/users/${shoppingUserId}/addresses/invoice/${addressId}`,
} satisfies {
    [k in keyof typeof hybrisUserDirectApi]: `/${string}` | ((...args: string[]) => `/${string}`);
};

const hybrisUserDirectApi = {
    getHybrisUserInfo: (spaToken: string) => async (shoppingUserId: string) => {
        const response = await getDirectHybrisAxiosClient(spaToken).get<HybrisUserInfo>(
            hybrisUserDirectApiUrls.getHybrisUserInfo(shoppingUserId),
        );
        return response && response.data;
    },
    updateHybrisUserInfo:
        (spaToken: string) =>
        async (
            shoppingUserId: string,
            redistributorOfFood: 'TRUE' | 'FALSE' | 'NOT_SET',
        ): Promise<boolean> => {
            const response = await getDirectHybrisAxiosClient(spaToken).patch(
                hybrisUserDirectApiUrls.updateHybrisUserInfo(shoppingUserId),
                null,
                { params: { redistributorOfFood } },
            );

            const resp = response && response.data;
            return resp;
        },
    getUserPunchoutUnit: (accessToken: string) => async (shoppingUserId: string) => {
        const response = await getDirectHybrisAxiosClient(
            accessToken,
        ).get<HybrisUserOrganisationUnit>(
            `${hybrisUserDirectApiUrls.getUserPunchoutUnit(shoppingUserId)}?fields=orgUnit`,
        );
        return response.data;
    },
    getAllAddresses: (accessToken: string) => async (shoppingUserId: string) => {
        const response = await getDirectHybrisAxiosClient(accessToken).get<{
            addresses: HybrisAddress[];
        }>(hybrisUserDirectApiUrls.getAllAddresses(shoppingUserId));
        return response.data.addresses;
    },
    getAllPunchoutAddresses:
        (accessToken: string) =>
        async (
            unitId: string,
        ): Promise<{
            addresses: HybrisAddress[];
        }> => {
            const response = await getDirectHybrisAxiosClient(accessToken).get(
                hybrisUserDirectApiUrls.getAllPunchoutAddresses(unitId),
            );
            return response.data;
        },
    createAddress:
        (accessToken: string) =>
        async (shoppingUserId: string, address: ApiCreateUserAddressRequest) => {
            const params = {
                firstName: address.firstName,
                lastName: address.lastName,
                email: address.email,
                line1: address.line1,
                town: address.town,
                postalCode: address.postalCode,
                phone: address.phone,
                stairs: address.stairs,
                doorCode: address.doorCode,
                companyName: address.companyName,
            };

            const response = await getDirectHybrisAxiosClient(accessToken, [
                hybrisFormValidationInterceptor,
            ]).post<HybrisAddress>(hybrisUserDirectApiUrls.createAddress(shoppingUserId), null, {
                params,
            });

            return response.data;
        },
    updateAddress:
        (accessToken: string) =>
        async (shoppingUserId: string, addressId: string, request: ApiUpdateUserAddressRequest) => {
            const formData = new URLSearchParams({
                id: addressId,
                firstName: request.firstName ?? '',
                lastName: request.lastName ?? '',
                email: request.email ?? '',
                line1: request.line1 ?? '',
                town: request.town ?? '',
                postalCode: request.postalCode ?? '',
                phone: request.phone ?? '',
                stairs: request.stairs ?? '',
                doorCode: request.doorCode ?? '',
                visibleInAddressBook: 'true',
                companyName: request.companyName ?? '',
            });

            await getDirectHybrisAxiosClient(accessToken, [hybrisFormValidationInterceptor]).put(
                hybrisUserDirectApiUrls.updateAddress(shoppingUserId, addressId),
                formData,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                },
            );
        },
    deleteAddress: (accessToken: string) => async (shoppingUserId: string, addressId: string) => {
        await getDirectHybrisAxiosClient(accessToken).delete(
            hybrisUserDirectApiUrls.deleteAddress(shoppingUserId, addressId),
        );
    },
    getInvoiceAddress: (accessToken: string) => async (shoppingUserId: string) => {
        const response = await getDirectHybrisAxiosClient(accessToken).get<HybrisUserInfo>(
            `${hybrisUserDirectApiUrls.getInvoiceAddress(shoppingUserId)}?fields=INVOICE`,
        );

        return response.data;
    },
    createInvoiceAddress:
        (accessToken: string) =>
        async (shoppingUserId: string, address: ApiCreateInvoiceAddressRequest) => {
            const params = {
                firstName: address.firstName,
                lastName: address.lastName,
                email: address.email,
                line1: address.line1,
                line2: address.line2,
                town: address.town,
                postalCode: address.postalCode,
                phone: address.phone,
                companyName: address.companyName,
                companyNumber: address.organizationNumber,
                reference: address.reference,
            };

            const response = await getDirectHybrisAxiosClient(accessToken, [
                hybrisFormValidationInterceptor,
            ]).post<HybrisUserInfo>(
                hybrisUserDirectApiUrls.createInvoiceAddress(shoppingUserId),
                null,
                {
                    params,
                },
            );

            return response.data;
        },
    updateInvoiceAddress:
        (accessToken: string) =>
        async (shoppingUserId: string, request: ApiUpdateInvoiceAddressRequest) => {
            const formData = new URLSearchParams({
                firstName: request.firstName ?? '',
                lastName: request.lastName ?? '',
                email: request.email ?? '',
                line1: request.line1 ?? '',
                line2: request.line2 ?? '',
                town: request.town ?? '',
                postalCode: request.postalCode ?? '',
                phone: request.phone ?? '',
                companyName: request.companyName ?? '',
                companyNumber: request.organizationNumber ?? '',
                reference: request.reference ?? '',
                addressId: request.id ?? '',
            });

            const response = await getDirectHybrisAxiosClient(accessToken, [
                hybrisFormValidationInterceptor,
            ]).put<InvoiceAddress>(
                hybrisUserDirectApiUrls.updateInvoiceAddress(shoppingUserId, request.id ?? ''),
                formData,
                {
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                },
            );

            return response.data;
        },
    getCustomerGroups:
        (spaToken: string) =>
        async (shoppingUserId: string): Promise<HybrisUserCustomerGroups> => {
            const response = await getDirectHybrisAxiosClient(
                spaToken,
            ).get<HybrisUserCustomerGroups>(
                hybrisUserDirectApiUrls.getCustomerGroups(shoppingUserId),
            );

            const resp = response && response.data;
            return resp;
        },
    getCompensationVouchers: async (
        spaToken: string,
        shoppingUserId: string,
    ): Promise<HybrisCompensationVoucher[]> => {
        const response = await getDirectHybrisAxiosClient(spaToken).get(
            hybrisUserDirectApiUrls.getCompensationVouchers(shoppingUserId),
            { params: { fields: 'FULL' } },
        );

        const resp = response && response.data && response.data.compensationVouchers;
        return resp || [];
    },
};

export { hybrisUserDirectApi };
